import React, { useContext, useState, useEffect } from "react";
import {
  doc,
  deleteDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  arrayRemove,
  writeBatch,
} from "firebase/firestore";
import { UserContext } from "../shared/UserContext";
import { firestore } from "../shared/FirebaseConfig";
import { useNavigate, Link } from "react-router-dom";
import InviteModal from "./InviteModal";
import { teacherDisplayName } from "./signoutData";
import { removeSampleClassroom } from "../shared/sampleData";
import "./ClassList.css";

const DisplayClasses = () => {
  const { user, schoolId } = useContext(UserContext);
  const [classList, setClassList] = useState([]);
  const [classPrefs, setClassPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedClassData, setSelectedClassData] = useState(null);
  const [schoolUsers, setSchoolUsers] = useState([]); // for reassigning a class
  const [countByClass, setCountByClass] = useState({}); // classId -> live enrolled count
  const [countsLoaded, setCountsLoaded] = useState(false); // gate the stale-array fallback
  const [removingSample, setRemovingSample] = useState(false);

  // Staff list for the "Reassign to…" control on owned classes.
  useEffect(() => {
    if (!schoolId) return;
    return onSnapshot(
      query(collection(firestore, "users"), where("schoolId", "==", schoolId)),
      snap => setSchoolUsers(snap.docs.map(d => ({ id: d.id, name: teacherDisplayName(d.data()) }))),
      err => console.error("[ClassList] users:", err),
    );
  }, [schoolId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ownerSnap, coSnap, otherSnap] = await Promise.all([
          getDocs(query(collection(firestore, "classes"), where("teacherId", "==", user?.uid))),
          getDocs(query(collection(firestore, "classes"), where("coTeachers", "array-contains", user?.uid))),
          getDocs(query(collection(firestore, "classes"), where("otherTeachers", "array-contains", user?.uid))),
        ]);

        const seen = new Set();
        const allClasses = [];
        for (const snap of [ownerSnap, coSnap, otherSnap]) {
          snap.docs.forEach((d) => {
            if (!seen.has(d.id)) {
              seen.add(d.id);
              allClasses.push({ id: d.id, ...d.data(), isOwner: d.data().teacherId === user?.uid });
            }
          });
        }

        setClassList(allClasses.filter((cls) => !cls.isArchived));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching classes:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.uid]);

  // Listen to this user's per-class preferences
  useEffect(() => {
    if (!user?.uid) return;
    return onSnapshot(doc(firestore, "users", user.uid), snap => {
      setClassPrefs(snap.data()?.classPrefs || {});
    });
  }, [user?.uid]);

  // Enrolled counts from the students' side (the source of truth) — class docs'
  // own `enrolledStudents` array can go stale (e.g. School Roster moves don't
  // reconcile it), so we count students whose enrolledClasses includes the class.
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId)))
      .then(snap => {
        const counts = {};
        snap.forEach(d => (d.data().enrolledClasses || []).forEach(cid => {
          counts[cid] = (counts[cid] || 0) + 1;
        }));
        if (!cancelled) { setCountByClass(counts); setCountsLoaded(true); }
      })
      .catch(err => console.error("[ClassList] enrolled counts:", err));
    return () => { cancelled = true; };
  }, [schoolId]);

  const handleInviteTeachers = (classData) => {
    setSelectedClassData(classData);
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setSelectedClassData(null);
  };

  const handleArchiveClass = async (classId) => {
    if (!window.confirm("Are you sure you want to archive this class?")) return;
    try {
      const classRef = doc(firestore, "classes", classId);
      await updateDoc(classRef, { isArchived: true });

      setClassList(classList.filter((cls) => cls.id !== classId));
    } catch (error) {
      console.error("Error archiving class:", error);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm(
      "Delete this class? The class and its history are removed. Students are NOT deleted — " +
      "they stay in the directory and any other classes, and just drop this one."
    )) return;
    try {
      // Detach the class from its students (the students' side is the source of
      // truth) — do NOT delete student docs: a shared student may be in other
      // classes. Remove the classId from enrolledClasses + clear any homeroom that
      // pointed here, so no dangling refs are left behind.
      const studentsSnap = await getDocs(query(
        collection(firestore, "students"),
        where("schoolId", "==", schoolId),
        where("enrolledClasses", "array-contains", classId)
      ));
      if (!studentsSnap.empty) {
        const batch = writeBatch(firestore);
        studentsSnap.forEach((d) => {
          const update = { enrolledClasses: arrayRemove(classId) };
          if (d.data().homeroomClassId === classId) update.homeroomClassId = null;
          batch.update(d.ref, update);
        });
        await batch.commit();
      }

      await deleteDoc(doc(firestore, "classes", classId));
      setClassList(classList.filter((cls) => cls.id !== classId));
    } catch (error) {
      console.error("Error deleting class:", error);
    }
  };

  const handleViewArchivedClasses = () => {
    navigate("/archivedclasses");
  };

  // Factory-reset the seeded demo classroom: removes the sample class AND all of its
  // sample data (students, sign-out history, attendance, library books, the
  // Chromebooks tracker) and reverts the library toggle. See sampleData.js.
  const handleRemoveSample = async () => {
    if (!window.confirm(
      "Remove the sample class and ALL of its demo data — sample students, sign-out " +
      "history, today's attendance, the sample library books and the Chromebooks " +
      "lending tracker? This can't be undone."
    )) return;
    setRemovingSample(true);
    try {
      await removeSampleClassroom(user.uid);
      setClassList((prev) => prev.filter((c) => !c.isSample));
    } catch (error) {
      console.error("Error removing sample data:", error);
      window.alert("Couldn't remove the sample data. Please try again.");
    } finally {
      setRemovingSample(false);
    }
  };

  // Hand a class off to another teacher (owner-only; rules allow the owner to edit
  // teacherId). After reassigning you no longer own it, so it drops from your list.
  const reassignClass = async (classData, newTeacherId) => {
    if (!newTeacherId || newTeacherId === classData.teacherId) return;
    const t = schoolUsers.find(u => u.id === newTeacherId);
    if (!window.confirm(`Reassign "${classData.className}" to ${t?.name || "this teacher"}? You'll no longer own this class.`)) return;
    try {
      await updateDoc(doc(firestore, "classes", classData.id), { teacherId: newTeacherId });
      setClassList(prev => prev.filter(c => c.id !== classData.id));
    } catch (err) {
      console.error("Error reassigning class:", err);
    }
  };

  const getPrefs = (classId) => ({
    attendanceEnabled:   classPrefs[classId]?.attendanceEnabled   ?? true,
    attendanceSortOrder: classPrefs[classId]?.attendanceSortOrder ?? Infinity,
  });

  const toggleAttendance = async (classId, currentEnabled) => {
    const newValue = !currentEnabled;
    try {
      await updateDoc(doc(firestore, "users", user.uid), {
        [`classPrefs.${classId}.attendanceEnabled`]: newValue,
      });
      setClassPrefs(prev => ({
        ...prev,
        [classId]: { ...prev[classId], attendanceEnabled: newValue },
      }));
    } catch (err) {
      console.error("Error toggling attendance:", err);
    }
  };

  // One homeroom per teacher: turning this class on clears the flag on the others.
  // The homeroom leads the attendance dropdown and is the teacher's emergency roster.
  const toggleHomeroom = async (classId, currentlyHomeroom) => {
    const newValue = !currentlyHomeroom;
    try {
      await updateDoc(doc(firestore, "classes", classId), { isHomeroom: newValue });
      if (newValue) {
        await Promise.all(
          classList
            .filter(c => c.id !== classId && c.isOwner && c.isHomeroom)
            .map(c => updateDoc(doc(firestore, "classes", c.id), { isHomeroom: false }))
        );
      }
      setClassList(prev => prev.map(c =>
        c.id === classId ? { ...c, isHomeroom: newValue }
          : (newValue && c.isOwner ? { ...c, isHomeroom: false } : c)
      ));
    } catch (err) {
      console.error("Error setting homeroom:", err);
    }
  };

  const moveClass = async (index, direction) => {
    const owned = [...classList]
      .filter(c => c.isOwner)
      .sort((a, b) => getPrefs(a.id).attendanceSortOrder - getPrefs(b.id).attendanceSortOrder);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= owned.length) return;
    const reordered = [...owned];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const updates = reordered.map((cls, i) => ({ id: cls.id, order: i }));
    const userUpdate = {};
    updates.forEach(({ id, order }) => {
      userUpdate[`classPrefs.${id}.attendanceSortOrder`] = order;
    });
    try {
      await updateDoc(doc(firestore, "users", user.uid), userUpdate);
      setClassPrefs(prev => {
        const next = { ...prev };
        updates.forEach(({ id, order }) => {
          next[id] = { ...next[id], attendanceSortOrder: order };
        });
        return next;
      });
    } catch (err) {
      console.error("Error reordering classes:", err);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="display-classes-container">
      <div className="classes-header">
        <h2>Your Classes</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to="/importclasses">
            <button className="btn-secondary">Import classes + students</button>
          </Link>
          <Link to="/addclass">
            <button>+ Add Class</button>
          </Link>
        </div>
      </div>

      {classList.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)" }}>No classes yet — create your first one to get started.</p>
      ) : (() => {
        const ownedClasses = [...classList]
          .filter(c => c.isOwner)
          .sort((a, b) => getPrefs(a.id).attendanceSortOrder - getPrefs(b.id).attendanceSortOrder);
        const coTaughtClasses = classList.filter(c => !c.isOwner);
        const allSorted = [...ownedClasses, ...coTaughtClasses];

        return (
          <div className="class-list">
            {allSorted.map((classData) => {
              const ownedIndex = ownedClasses.findIndex(c => c.id === classData.id);
              return (
                <div
                  key={classData.id}
                  className="class-item"
                  data-tour={classData.isSample ? "sample-class" : undefined}
                >
                  <div className="class-info">
                    <h3>
                      {classData.className}
                      {classData.isSample && (
                        <span className="badge" style={{ color: "#fff", background: "#7c3aed" }}>
                          ✨ Sample
                        </span>
                      )}
                      {!classData.isOwner && (
                        <span className="badge" style={{ color: "#fff", background: "var(--color-primary)" }}>
                          Co-Teacher
                        </span>
                      )}
                      {classData.classType === "specials" && (
                        <span className="badge" style={{ color: "#fff", background: "#0f766e" }} title="Pull-out class — won't change master rosters">
                          🔗 Specials
                        </span>
                      )}
                    </h3>
                    {classData.isSample && (
                      <p style={{ color: "var(--color-secondary)", fontStyle: "italic" }}>
                        A demo class to explore every feature — remove it whenever you're ready for your real students.
                      </p>
                    )}
                    <p>
                      School Year: {classData.schoolYear} | Grade:{" "}
                      {classData.gradeLevel}
                    </p>
                    <p>
                      Enrolled Students: {countsLoaded ? (countByClass[classData.id] || 0) : (classData.enrolledStudents?.length || 0)}
                    </p>
                  </div>
                  <div className="class-actions">
                    <button onClick={() => navigate("/displayenrolledstudents", { state: { classId: classData.id } })}>
                      View Students
                    </button>
                    <button className="btn-secondary" onClick={() => navigate("/displayenrolledstudents", { state: { classId: classData.id, openAdd: true } })}>
                      Add Students
                    </button>
                    <button className="btn-secondary" onClick={() => handleInviteTeachers(classData)}>
                      Invite Teachers
                    </button>
                    {classData.isOwner && (
                      <button className="btn-secondary" onClick={() => navigate(`/editclass/${classData.id}`)}>
                        Edit
                      </button>
                    )}
                    {classData.isOwner && !classData.isSample && (
                      <button className="btn-ghost" onClick={() => handleArchiveClass(classData.id)}>
                        Archive
                      </button>
                    )}
                    {classData.isOwner && schoolUsers.length > 1 && (
                      <select
                        value=""
                        onChange={(e) => { reassignClass(classData, e.target.value); e.target.value = ""; }}
                        title="Reassign this class to another teacher"
                        style={{ width: "auto" }}
                      >
                        <option value="">Reassign to…</option>
                        {schoolUsers.filter(u => u.id !== classData.teacherId).map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    )}
                    {classData.isOwner && !classData.isSample && (
                      <button
                        className="red-button"
                        onClick={() => handleDeleteClass(classData.id)}
                      >
                        Delete
                      </button>
                    )}
                    {classData.isSample && (
                      <button
                        className="red-button"
                        onClick={handleRemoveSample}
                        disabled={removingSample}
                      >
                        {removingSample ? "Removing…" : "Remove sample class & data"}
                      </button>
                    )}
                  </div>
                  {classData.isOwner && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 20, fontSize: 13, flexWrap: "wrap" }}>
                      {classData.classType === "specials" ? (
                        <span
                          title="Pull-out class — won't change master rosters; it's never a homeroom"
                          style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--color-secondary)", fontWeight: 400 }}
                        >
                          <span style={{ fontSize: 16, lineHeight: 1 }}>🔗</span>
                          Specials / pull-out
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleHomeroom(classData.id, !!classData.isHomeroom)}
                          aria-pressed={!!classData.isHomeroom}
                          title={classData.isHomeroom
                            ? "This is your homeroom — sorts to the top for attendance and is your emergency roster"
                            : "Set as your homeroom (sorts to the top for attendance and is your emergency roster)"}
                          style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "none", border: "none", padding: 0,
                            cursor: "pointer", userSelect: "none", fontSize: 13,
                            color: classData.isHomeroom ? "var(--color-primary)" : "var(--color-secondary)",
                            fontWeight: classData.isHomeroom ? 600 : 400,
                          }}
                        >
                          <span style={{ fontSize: 16, lineHeight: 1 }}>{classData.isHomeroom ? "⭐" : "☆"}</span>
                          Homeroom
                        </button>
                      )}
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
                        <input
                          type="checkbox"
                          checked={getPrefs(classData.id).attendanceEnabled}
                          onChange={() => toggleAttendance(classData.id, getPrefs(classData.id).attendanceEnabled)}
                        />
                        Attendance
                      </label>
                      {getPrefs(classData.id).attendanceEnabled && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--color-secondary)" }}>
                          <span>Dropdown order:</span>
                          <button
                            onClick={() => moveClass(ownedIndex, -1)}
                            disabled={ownedIndex <= 0}
                            style={{ padding: "2px 8px", fontSize: 13, lineHeight: 1 }}
                            title="Move up in attendance dropdown"
                          >↑</button>
                          <button
                            onClick={() => moveClass(ownedIndex, 1)}
                            disabled={ownedIndex >= ownedClasses.length - 1}
                            style={{ padding: "2px 8px", fontSize: 13, lineHeight: 1 }}
                            title="Move down in attendance dropdown"
                          >↓</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      <div className="classes-footer">
        <button className="btn-ghost" onClick={handleViewArchivedClasses}>View Archived Classes</button>
      </div>

      {isInviteModalOpen && (
        <InviteModal
          onClose={handleCloseInviteModal}
          user={user}
          classData={selectedClassData}
          schoolId={schoolId}
        />
      )}
    </div>
  );
};

export default DisplayClasses;
